import { useEffect, useRef, useState } from 'react'

export interface UseCountUpOptions {
	/** Starting value for the initial animation (defaults to 0, or previous target when updating) */
	start?: number
	/** Duration of animation in milliseconds (defaults to 600) */
	duration?: number
	/** Whether the animation is enabled (defaults to true in browser, false in test environments and
	 *  when the viewer asked for reduced motion) */
	enabled?: boolean
	/** Easing function mapping progress (0-1) to eased value (0-1). Defaults to cubic ease-out. */
	easing?: (progress: number) => number
	/** Number of decimal places to preserve (defaults to 0) */
	decimals?: number
	/** Callback fired when animation finishes */
	onEnd?: () => void
	/** Count up only until the first animation finishes; later targets land in place (defaults to false) */
	firstOnly?: boolean
	/** Identifies the value across remounts, so `firstOnly` survives a panel leaving and re-entering view */
	animationKey?: string
}

const defaultEaseOutCubic = (t: number): number => 1 - (1 - t) ** 3
const isTestEnv = typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'test'

/**
 * Values that have finished counting, by key. A dashboard panel unmounts its content once it is
 * scrolled far enough away, so a flag held in the component would let the count replay on the way
 * back. These keys outlive the remount, and reset with the page.
 */
const countedKeys = new Set<string>()

export function hasCountedOnce(key: string | undefined): boolean {
	return key !== undefined && countedKeys.has(key)
}

export function markCountedOnce(key: string | undefined): void {
	if (key !== undefined) {
		countedKeys.add(key)
	}
}

export function prefersReducedMotion(): boolean {
	// jsdom and older browsers have no matchMedia.
	return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
		? window.matchMedia('(prefers-reduced-motion: reduce)').matches
		: false
}

/**
 * Smoothly animates a number from its previous value to a target value using requestAnimationFrame.
 */
export function useCountUp(target: number, options?: UseCountUpOptions | number, maybeEnabled?: boolean): number {
	const resolvedOptions: UseCountUpOptions =
		typeof options === 'number' ? { duration: options, enabled: maybeEnabled } : (options ?? {})

	const {
		start: initialStart,
		duration = 600,
		enabled = !isTestEnv && !prefersReducedMotion(),
		easing = defaultEaseOutCubic,
		decimals = 0,
		onEnd,
		firstOnly = false,
		animationKey,
	} = resolvedOptions

	const [displayValue, setDisplayValue] = useState(enabled ? (initialStart ?? 0) : target)
	const prevTargetRef = useRef(initialStart ?? 0)
	const onEndRef = useRef(onEnd)
	onEndRef.current = onEnd
	// Set on completion, not on start: StrictMode cancels the first run's frame before it lands.
	const countedRef = useRef(false)

	useEffect(() => {
		if (!enabled || (firstOnly && (countedRef.current || hasCountedOnce(animationKey)))) {
			setDisplayValue(target)
			prevTargetRef.current = target
			return
		}

		const start = prevTargetRef.current
		const end = target
		prevTargetRef.current = target

		if (start === end) {
			setDisplayValue(end)
			return
		}

		let startTime: number | null = null
		let rafId: number

		const factor = 10 ** decimals

		const step = (timestamp: number) => {
			if (startTime === null) {
				startTime = timestamp
			}
			const progress = Math.min((timestamp - startTime) / duration, 1)
			const eased = easing(progress)
			const rawValue = start + (end - start) * eased
			// The last frame lands on the target itself. Rounding it too would leave a target carrying
			// more precision than `decimals` settling on an approximation of it.
			let current = end
			if (progress < 1) {
				current = decimals > 0 ? Math.round(rawValue * factor) / factor : Math.round(rawValue)
			}
			setDisplayValue(current)

			if (progress < 1) {
				rafId = requestAnimationFrame(step)
			} else {
				countedRef.current = true
				markCountedOnce(animationKey)
				onEndRef.current?.()
			}
		}

		rafId = requestAnimationFrame(step)
		return () => cancelAnimationFrame(rafId)
	}, [target, duration, enabled, easing, decimals, firstOnly, animationKey])

	return displayValue
}
